<template>
  <div>
    <div class="flex flex-row w-full text-black font-bold justify-end text-xs">
      <div>{{ $t('last_update') }}: {{ formatDate(panel1.cache_time) }}</div>
      <div class="ml-3">{{ $t('next_update') }}: {{ formatDate(panel1.next_refresh_time) }}</div>
    </div>

    <!-- info box -->
    <div class="w-full mt-8">
      <div class="flex flex-row w-full text-blue font-bold">{{ $t('project_information') }}</div>
      <div v-if="panel1.info" class="flex flex-wrap justify-center w-100">
        <div v-for="(box, index) in panel1.info" :style="'height: 160px'" class="w-1/4 mb-2" :key="'panel1_'+index">
          <div class="border-4 border-blue text-blue rounded flex items-center h-full justify-center flex-col mx-2">
            <b class="text-center">{{ box.name }}</b>
            <b class="text-3xl">{{ box.total }}</b>
          </div>
        </div>
      </div>
    </div>

    <!-- table users info -->
    <div class="w-full mt-8">
      <div class="flex flex-row w-full items-center mb-2">
        <div class="flex-1 text-blue font-bold">{{ $t('information_per_user') }}</div>
        <v-button
          type="button"
          color="green"
          icon="file-excel"
          icon-pack="fas"
          :loading="isExporting"
          :disabled="!hasUsers"
          @click="exportUsersToExcel"
        >
          {{ $t('export') }}
        </v-button>
      </div>

      <b-table
        icon-pack="fas"
        :data="panel1.users"
        :bordered="true"
        v-if="panel1.users"
      >
        <template>
          <b-table-column field="avatar" cell-class="w-10">
            <template v-slot:header=""><span class="text-xs">Avatar</span></template>
            <template v-slot="props">
              <div class="w-8 h-8 rounded-full">
                <img class="w-8 h-8 rounded-full" :src="props.row.avatar"/>
              </div>
            </template>
          </b-table-column>

          <b-table-column field="fullname" cell-class="w-5/12">
            <template v-slot:header=""><span class="text-xs">{{ $t('user') }}</span></template>
            <template v-slot="props">{{ props.row.fullname }}</template>
          </b-table-column>

          <b-table-column field="assignment_records">
            <template v-slot:header=""><span class="text-xs">{{ $t('assignment_records') }}</span></template>
            <template v-slot="props">{{ props.row.assignment_records }}</template>
          </b-table-column>

          <b-table-column field="comments">
            <template v-slot:header=""><span class="text-xs">{{ $t('comments') }}</span></template>
            <template v-slot="props">{{ props.row.comments }}</template>
          </b-table-column>

          <b-table-column field="images">
            <template v-slot:header=""><span class="text-xs">{{ $t('images') }}</span></template>
            <template v-slot="props">{{ props.row.images }}</template>
          </b-table-column>

          <b-table-column field="documents">
            <template v-slot:header=""><span class="text-xs">{{ $t('documents') }}</span></template>
            <template v-slot="props">{{ props.row.documents }}</template>
          </b-table-column>

          <b-table-column field="create_issues">
            <template v-slot:header=""><span class="text-xs">{{ $t('issues_created') }}</span></template>
            <template v-slot="props">{{ props.row.create_issues }}</template>
          </b-table-column>

          <b-table-column field="issues">
            <template v-slot:header=""><span class="text-xs">{{ $t('issues_participant') }}</span></template>
            <template v-slot="props">{{ props.row.issues }}</template>
          </b-table-column>
        </template>

        <template #empty>
          <div class="has-text-centered">{{ $t('no_results') }}</div>
        </template>
      </b-table>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import * as XLSX from 'xlsx'

const DATE_DISPLAY_FORMAT = 'HH:mm (DD-MM)'
const EXCEL_FILE_EXTENSION = '.xlsx'
const EXCEL_SHEET_NAME_MAX = 31 // Excel limit for sheet names
const EXCEL_COL_MIN_WIDTH = 12
const EXCEL_COL_MAX_WIDTH = 50
const EXCEL_COL_PADDING = 2

export default {
  name: 'project-information-panel',

  props: {
    panel1: {
      type: Object,
      required: true,
      default: () => ({ info: [], users: [], cache_time: null, next_refresh_time: null })
    },
    exportFilename: { type: String, default: 'project_information' },
  },

  data: () => ({
    isExporting: false,
  }),

  computed: {
    hasUsers() {
      return Array.isArray(this.panel1.users) && this.panel1.users.length > 0
    },
  },

  methods: {
    formatDate(date) {
      return moment(date).format(DATE_DISPLAY_FORMAT)
    },

    buildExcelRows() {
      return this.panel1.users.map(user => ({
        [this.$t('user')]: user.fullname,
        [this.$t('assignment_records')]: user.assignment_records,
        [this.$t('comments')]: user.comments,
        [this.$t('images')]: user.images,
        [this.$t('documents')]: user.documents,
        [this.$t('issues_created')]: user.create_issues,
        [this.$t('issues_participant')]: user.issues,
      }))
    },

    calculateColumnWidths(rows) {
      const headers = Object.keys(rows[0])
      return headers.map(header => {
        const maxValueLen = rows.reduce((max, row) => {
          const value = row[header] == null ? '' : String(row[header])
          return Math.max(max, value.length)
        }, header.length)
        const width = Math.min(EXCEL_COL_MAX_WIDTH, Math.max(EXCEL_COL_MIN_WIDTH, maxValueLen + EXCEL_COL_PADDING))
        return { wch: width }
      })
    },

    applyNumberFormat(worksheet, rows) {
      const numericFields = new Set([
        this.$t('assignment_records'),
        this.$t('comments'),
        this.$t('images'),
        this.$t('documents'),
        this.$t('issues_created'),
        this.$t('issues_participant'),
      ])
      const headers = Object.keys(rows[0])
      headers.forEach((header, colIdx) => {
        if (!numericFields.has(header)) return
        for (let rowIdx = 1; rowIdx <= rows.length; rowIdx++) {
          const cellRef = XLSX.utils.encode_cell({ r: rowIdx, c: colIdx })
          const cell = worksheet[cellRef]
          if (cell) cell.z = '0'
        }
      })
    },

    exportUsersToExcel() {
      if (this.isExporting) return

      if (!this.hasUsers) {
        this.$notify.error('no_results')
        return
      }

      this.isExporting = true

      try {
        const rows = this.buildExcelRows()
        const worksheet = XLSX.utils.json_to_sheet(rows)

        // Ancho de columnas auto-ajustado al contenido
        worksheet['!cols'] = this.calculateColumnWidths(rows)

        // Fija la fila de cabeceras al hacer scroll
        worksheet['!views'] = [{ ySplit: 1, topLeftCell: 'A2', state: 'frozen' }]

        this.applyNumberFormat(worksheet, rows)

        const sheetName = this.$t('information_per_user').substring(0, EXCEL_SHEET_NAME_MAX)
        const workbook = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(workbook, worksheet, sheetName)

        const filename = this.exportFilename + '_' + moment().format('YYYYMMDD_HHmm') + EXCEL_FILE_EXTENSION
        XLSX.writeFile(workbook, filename)
      } catch (error) {
        this.$notify.error('error_export')
        console.log('Error exporting users to Excel', error)
      } finally {
        this.isExporting = false
      }
    },
  },
}
</script>
